import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, LogOut, Eye, EyeOff } from 'lucide-react';
import VotingHeader from './VotingHeader';
import { getRootSurvey, requestNomineePin, getNomineeVotes } from '../api/voting';

export default function MyVotes() {
  const [survey, setSurvey]     = useState(null);
  const [settings, setSettings] = useState(null);
  const [code, setCode]         = useState('');
  const [pin, setPin]           = useState('');
  const [showPin, setShowPin]   = useState(false);
  const [result, setResult]     = useState(null);
  const [busy, setBusy]         = useState('');   // '' | 'pin' | 'votes'
  const [notice, setNotice]     = useState(null); // { type: 'success' | 'danger', text }
  const [loaded, setLoaded]     = useState(false);

  useEffect(() => {
    getRootSurvey()
      .then((res) => {
        const s   = res?.data?.survey;
        const cfg = res?.data?.settings;
        setSurvey(s);
        setSettings(cfg);
        if (cfg) {
          const root = document.documentElement;
          if (cfg.primary_color)    root.style.setProperty('--color-primary',    cfg.primary_color);
          if (cfg.secondary_color)  root.style.setProperty('--color-secondary',  cfg.secondary_color);
          if (cfg.background_color) {
            root.style.setProperty('--color-background', cfg.background_color);
            document.body.style.backgroundColor = cfg.background_color;
          }
          if (cfg.text_color) root.style.setProperty('--color-text', cfg.text_color);
        }
        if (s?.surveyname) document.title = 'My Votes — ' + s.surveyname;
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const enabled = Number(survey?.show_votes_online) === 1;

  useEffect(() => {
    if (loaded && !enabled) window.location.replace('/');
  }, [loaded, enabled]);

  const errorText = (e) => e?.response?.status === 429
    ? 'Too many attempts. Please wait a few minutes and try again.'
    : (e?.response?.data?.message || e?.message || 'Network error.');

  const handleRequestPin = async () => {
    if (code.length !== 4) { setNotice({ type: 'danger', text: 'Enter your 4-digit nominee code first.' }); return; }
    setBusy('pin'); setNotice(null);
    try {
      const res = await requestNomineePin(code);
      setNotice({ type: res?.status === 'success' ? 'success' : 'danger', text: res?.message || 'Could not send PIN.' });
    } catch (e) {
      setNotice({ type: 'danger', text: errorText(e) });
    } finally {
      setBusy('');
    }
  };

  const loadVotes = async () => {
    setBusy('votes'); setNotice(null);
    try {
      const res = await getNomineeVotes(code, pin);
      if (res?.status === 'success') setResult(res.data);
      else { setResult(null); setNotice({ type: 'danger', text: res?.message || 'Invalid nominee code or PIN.' }); }
    } catch (e) {
      setNotice({ type: 'danger', text: errorText(e) });
    } finally {
      setBusy('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length !== 4 || pin.length !== 5) {
      setNotice({ type: 'danger', text: 'Enter your 4-digit nominee code and 5-character PIN.' });
      return;
    }
    loadVotes();
  };

  const handleLogout = () => { setResult(null); setPin(''); setNotice(null); };

  return (
    <div className="content-voting min-vh-100 d-flex flex-column">
      <div className="container-xl px-3 px-md-4 pt-3 pb-3 flex-grow-1">

        <VotingHeader survey={survey} settings={settings} label="My Votes" showDescription={false} bordered={false} />

        <div className="my-votes">
          {!loaded || !enabled ? (
            <div className="d-flex justify-content-center py-5">
              <Loader2 size={32} className="text-warning spin" />
            </div>
          ) : (
          <>
          {notice && (
            <div className={`alert alert-${notice.type} small py-2`} role="alert">{notice.text}</div>
          )}

          {!result ? (
            <form className="card my-votes__card" onSubmit={handleSubmit}>
              <div className="card-body p-4">
                <p className="form-eyebrow">Nominees</p>
                <h2 className="form-main-title">Check your votes</h2>
                <p className="small text-muted mb-4">Enter your nominee code and PIN.</p>

                <label className="form-label small fw-semibold" htmlFor="mv-code">Nominee Code</label>
                <input
                  id="mv-code"
                  className="form-control mb-3 font-mono"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="e.g. 0103"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />

                <label className="form-label small fw-semibold" htmlFor="mv-pin">PIN</label>
                <div className="input-group mb-4">
                  <input
                    id="mv-pin"
                    type={showPin ? 'text' : 'password'}
                    className="form-control font-mono"
                    maxLength={5}
                    autoComplete="off"
                    placeholder="•••••"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/[^a-z0-9]/gi, '').toUpperCase())}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPin((v) => !v)}
                    aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                <button type="submit" className="btn btn-submit text-white w-100" disabled={!!busy}>
                  {busy === 'votes' ? <Loader2 size={18} className="spin" /> : 'View Votes'}
                </button>

                <p className="small text-center mt-3 mb-0">
                  Don't have a PIN?{' '}
                  <button type="button" className="btn btn-link btn-sm p-0 my-votes__link" onClick={handleRequestPin} disabled={!!busy}>
                    {busy === 'pin' ? 'Sending…' : 'Request PIN'}
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h2 className="form-main-title my-votes__hello mb-0">Hello, {result.nominee.name}</h2>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={loadVotes} disabled={!!busy}>
                    <RefreshCw size={14} className={busy === 'votes' ? 'spin' : ''} /> Refresh
                  </button>
                  <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={handleLogout}>
                    <LogOut size={14} /> Log out
                  </button>
                </div>
              </div>

              <div className="my-votes__results">
              {busy === 'votes' && (
                <div className="my-votes__overlay">
                  <Loader2 size={32} className="spin" />
                </div>
              )}
              {result.categories.map((cat) => (
                <div className="card my-votes__card mb-3" key={cat.id}>
                  <div className="my-votes__cat">
                    <span className="my-votes__cat-name">{cat.name}</span>
                    <span className="my-votes__cat-label my-votes__code-col">Code</span>
                    <span className="my-votes__cat-label my-votes__count-col">Votes</span>
                  </div>
                  <ol className="my-votes__list">
                    {cat.nominees.map((n, i) => (
                      <li key={i} className={`my-votes__row${n.is_me ? ' my-votes__me' : ''}`}>
                        <span className="my-votes__rank">{i + 1}</span>
                        <span className="my-votes__name">
                          {n.name}
                          {n.is_me && <span className="my-votes__you">You</span>}
                        </span>
                        <span className="my-votes__code">{n.code}</span>
                        <span className="my-votes__count">{n.votes.toLocaleString()}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
              </div>
            </>
          )}
          </>
          )}
        </div>

      </div>

      <footer className="text-center py-3 small credits-text">
        Powered by <a href="https://etuzo.co.ke" className="credits-link">eTuzo</a>
      </footer>
    </div>
  );
}
